import {
  BellOutlined,
  ContactsOutlined,
  ContainerOutlined,
  ControlOutlined,
  DeliveredProcedureOutlined,
  FileOutlined,
  FundProjectionScreenOutlined,
  FundViewOutlined,
  InsertRowAboveOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Avatar, Badge, Dropdown, Input, Layout, Menu, Modal, Spin } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthProvider';
import { updateField } from '../../contextRedux/User/UserModel';
import administrare from '../../images/dashboard/administrare.png';
import NotificationService from '../../services/notification/NotificationService';
import UserService from '../../services/user/UserService';
import { RootState } from '../../store';
import {
  canAccessUsersMenu,
  CRM_DEFAULT_ROUTE,
  CRM_ONLY_MODE,
  getCrmSelectedMenuKey,
  hasFullCrmAccess,
  hasLimitedCrmAccess,
  isCrmAllowedRoute,
} from '../../utils/crmAccess';
import '../Dashboard/Dashboard.css';
import SignaturePad from '../SignaturePad/SignaturePad';

interface DashboardProps {
  children: React.ReactNode;
}

const { Header, Sider, Content } = Layout;

const Dashboard: FC<DashboardProps> = ({ children }) => {
  const { profile } = useAuth();
  const { message } = App.useApp();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const location = useLocation();
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [openKeys, setOpenKeys] = useState<string[]>([]); // Track open submenus
  const logo = '/Logo.jpeg';
  const userRoles = Array.isArray(profile?.userPermission?.rols) ? (profile.userPermission.rols as string[]) : [];
  const roleAliases: Record<string, string[]> = {
    technician: ['tehnician'],
    tehnician: ['technician'],
  };
  const packages = profile?.userPermission?.packages;
  const roles = Array.from(
    new Set(userRoles.map((role) => role.trim().toLowerCase()).flatMap((role) => [role, ...(roleAliases[role] ?? [])])),
  );
  const crmHasFullAccess = hasFullCrmAccess(roles);
  const crmHasLimitedAccess = !crmHasFullAccess && hasLimitedCrmAccess(roles);
  const crmCanAccessUsers = canAccessUsersMenu(roles);
  const canAccessErpMenu = roles.includes('admin') || roles.includes('accountant');
  const isTechnician = roles.includes('tehnician');
  const isSeniorTechnician = roles.includes('senior_tehnician');
  const canAccessServiceMaintenanceMenu = crmHasFullAccess || crmHasLimitedAccess;

  const [isPasswordModalVisible, setPasswordModalVisible] = useState<boolean>(false);
  const [isSignatureModalVisible, setSignatureModalVisible] = useState<boolean>(false);

  const signature = useSelector((state: RootState) => state.models.user.signature);
  const password = useSelector((state: RootState) => state.models.user.password);

  // Fetch notification count
  const { data: notificationCount = 0 } = useQuery({
    queryKey: ['notificationCount'],
    queryFn: () => NotificationService.count(),
    refetchInterval: 30000, // Refetch every 30 seconds
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const isDashboardOpen = localStorage.getItem('isDashboardOpen') === 'true';
    setCollapsed(!isDashboardOpen);
    setLoading(false);

    // Retrieve openKeys from localStorage
    const storedOpenKeys = JSON.parse(localStorage.getItem('openKeys') || '[]');
    setOpenKeys(storedOpenKeys);
  }, []);

  useEffect(() => {
    const currentPath = CRM_ONLY_MODE
      ? getCrmSelectedMenuKey(location.pathname)
      : localStorage.getItem('selectedMenuKey') || location.pathname;
    setSelectedKey(currentPath);
  }, [location.pathname]);

  const handleMenuClick = (e: { key: string; keyPath: string[] }) => {
    localStorage.setItem('selectedMenuKey', e.key === '/logout' ? '' : e.key);
    setSelectedKey(e.key);

    const parentKeys = e.keyPath.slice(1); // Get all parent keys

    setOpenKeys(parentKeys); // Keep the clicked parent's keys open
    localStorage.setItem('openKeys', JSON.stringify(parentKeys));
  };

  const onOpenChange = (keys: string[]) => {
    // Control submenu behavior by allowing only one open at a time
    if (keys.length > 0) {
      setOpenKeys(keys); // Allow opening of multiple, but manage to show parent keys
      localStorage.setItem('openKeys', JSON.stringify(keys));
    } else {
      setOpenKeys([]);
      localStorage.setItem('openKeys', '[]');
    }
  };

  const toggle = () => {
    const updatedCollapsed = !collapsed;
    setCollapsed(updatedCollapsed);
    localStorage.setItem('isDashboardOpen', String(!updatedCollapsed));
  };

  const handleRestPassword = () => {
    setPasswordModalVisible(true);
  };

  const handleRestSignature = () => {
    setSignatureModalVisible(true);
  };

  const handlePasswordOk = () => {
    setPasswordModalVisible(false);
    updatePassword(password);
  };

  const handleSignatureOk = () => {
    setSignatureModalVisible(false);
    updateSignature(signature);
  };

  const handleCancel = () => {
    setPasswordModalVisible(false);
    setSignatureModalVisible(false);
    dispatch(updateField({ field: 'signature', value: '' }));
    dispatch(updateField({ field: 'password', value: '' }));
  };

  const { mutate: updateSignature, isPending: isUpdatingSignature } = useMutation({
    mutationFn: (formData: any) => UserService.updateSignature(formData),
    onSuccess: (data: any) => {
      if (data) {
        message.success(
          <span>
            <span className='font-weight-bold'> {'Semnatura a fost modificata cu succes!'}</span>
          </span>,
        );
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    },
  });

  const { mutate: updatePassword, isPending: isUpdatingPassword } = useMutation({
    mutationFn: (formData: any) => UserService.updatePassword(formData),
    onSuccess: (data: any) => {
      if (data) {
        message.success(
          <span>
            <span className='font-weight-bold'>{'Parola a fost modificata cu succes!'}</span>
          </span>,
        );
        setTimeout(() => {
          data?.response && navigate('/logout');
        }, 2000);
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    },
  });

  const userProfileMenu = [
    {
      key: '0',
      label: (
        <span>
          {'Username: '}
          <span className='font-weight-bold'>{profile && profile.name}</span>
        </span>
      ),
    },
    {
      key: '1',
      label: (
        <span style={{ color: 'red' }} onClick={() => handleRestPassword()}>
          Resetare parola
        </span>
      ),
    },
    {
      key: '2',
      label: (
        <span style={{ color: 'red' }} onClick={() => handleRestSignature()}>
          Resetare semnatura
        </span>
      ),
    },
    {
      key: '3',
      label: (
        <Link to='/logout' style={{ color: 'red' }}>
          Logout
        </Link>
      ),
    },
  ];

  const fallbackMenuItems = [
    ...(packages?.sm &&
    (roles?.includes('senior_tehnician') ||
      roles?.includes('admin') ||
      roles?.includes('senior_sales') ||
      roles?.includes('tehnician') ||
      roles?.includes('accountant') ||
      roles?.includes('dispatcher') ||
      roles?.includes('pre-sales'))
      ? [
          {
            key: '/panel',
            icon: <img src={administrare} style={{ height: 15 }} alt='logo' className='logo-image' />,
            label: <div>CRM</div>,
            children: [
              {
                key: 'sub3',
                icon: <FileOutlined />,
                label: 'Service & Mentenanta',
                children: [
                  ...(roles?.includes('admin') || roles?.includes('senior_sales')
                    ? [
                        {
                          key: '/service-maintenance/offer',
                          icon: <ContainerOutlined />,
                          label: <Link to='/service-maintenance/offer/active-offers'>Oferte</Link>,
                        },
                        (roles?.includes('admin') || roles?.includes('senior_sales')) && {
                          key: '/service-maintenance/contract',
                          icon: <DeliveredProcedureOutlined />,
                          label: <Link to='/service-maintenance/contract/active-contracts'>Contracte</Link>,
                        },
                      ]
                    : []),
                  ...(roles?.includes('tehnician') ||
                  roles?.includes('dispatcher') ||
                  roles?.includes('senior_tehnician') ||
                  roles?.includes('sales') ||
                  roles?.includes('senior_sales') ||
                  roles?.includes('accountant') ||
                  roles?.includes('admin') ||
                  roles?.includes('pre-sales')
                    ? [
                        {
                          key: 'sub31',
                          icon: <ContainerOutlined />,
                          label: <>Dispecerat</>,
                          children: [
                            (roles?.includes('tehnician') ||
                              roles?.includes('dispatcher') ||
                              roles?.includes('senior_tehnician') ||
                              roles?.includes('admin')) && {
                              key: '/service-maintenance/dispatch/dispatch',
                              label: <Link to='/service-maintenance/dispatch/dispatch'>Solicitari</Link>,
                            },
                            (roles?.includes('dispatcher') || roles?.includes('senior_tehnician')) && {
                              key: '/service-maintenance/dispatch/maintenancePlanning',
                              label: (
                                <Link to='/service-maintenance/dispatch/maintenancePlanning'>
                                  Planificare Mentenante
                                </Link>
                              ),
                            },
                            (roles?.includes('dispatcher') ||
                              roles?.includes('tehnician') ||
                              roles?.includes('senior_tehnician')) && {
                              key: '/service-maintenance/dispatch/myMaintenancePlannings',
                              label: (
                                <Link to='/service-maintenance/dispatch/myMaintenancePlannings'>Mentenantele Mele</Link>
                              ),
                            },
                            (roles?.includes('dispatcher') ||
                              roles?.includes('tehnician') ||
                              roles?.includes('senior_tehnician') ||
                              roles?.includes('accountant')) && {
                              key: '/service-maintenance/interventionFiles',
                              label: <Link to='/service-maintenance/interventionFiles'>Fise de Interventie</Link>,
                            },
                            (roles?.includes('accountant') || roles?.includes('pre-sales')) && {
                              key: '/service-maintenance/interventionFile/supplies',
                              label: <Link to='/service-maintenance/interventionFile/supplies'>Consumabile</Link>,
                            },
                            (roles?.includes('sales') ||
                              roles?.includes('senior_sales') ||
                              roles?.includes('pre-sales')) && {
                              key: '/service-maintenance/interventionFile/recommendations',
                              label: (
                                <Link to='/service-maintenance/interventionFile/recommendations'>Recomandari</Link>
                              ),
                            },
                          ],
                        },
                      ]
                    : []),
                ].filter(Boolean),
              },
            ],
          },
        ]
      : []),
    ...((packages?.pm &&
      (roles?.includes('senior_tehnician') || roles?.includes('admin') || roles?.includes('senior_sales'))) ||
    roles?.includes('accountant')
      ? [
          {
            key: 'sub4',
            icon: <FileOutlined />,
            label: 'Project Management',
            children: [
              ...(roles?.includes('senior_tehnician') || roles?.includes('admin') || roles?.includes('senior_sales')
                ? [
                    {
                      key: '/project-management/pmOffer',
                      icon: <ContainerOutlined />,
                      label: <Link to='/project-management/pmOffer/active-pmoffers'>Oferte Instalari</Link>,
                    },
                    (roles?.includes('senior_tehnician') || roles?.includes('tehnician')) &&
                      (roles?.includes('admin') || roles?.includes('senior_sales')) && {
                        key: '/project-management/pmContract',
                        icon: <DeliveredProcedureOutlined />,
                        label: <Link to='/project-management/pmContract/active-pmContracts'>Contracte Instalari</Link>,
                      },
                    ...(roles?.includes('senior_tehnician') && roles?.includes('dispatcher')
                      ? [
                          {
                            key: '/project-management/pmOfferProject',
                            icon: <DeliveredProcedureOutlined />,
                            label: (
                              <Link to='/project-management/pmOfferProject/active-pmOfferProjects'>Oferte Proiect</Link>
                            ),
                          },
                          {
                            key: '/project-management/pmContractProject',
                            icon: <DeliveredProcedureOutlined />,
                            label: (
                              <Link to='/project-management/pmContractProject/active-pmContractProjects'>
                                Contracte Proiect
                              </Link>
                            ),
                          },
                        ]
                      : []),
                  ]
                : []),
              // {
              //   key: '/project-management/interventionFileProjects',
              //   icon: <ContainerOutlined />,
              //   label: <Link to='/project-management/interventionFileProjects'>Fise sef de echipa</Link>,
              // },
              ...(roles?.includes('admin')
                ? [
                    {
                      key: '/project-management/financial-report',
                      icon: <ContainerOutlined />,
                      label: <Link to='/project-management/financial-report'>Raport Financiar</Link>,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(packages?.accessControl && roles?.includes('admin')
      ? [
          {
            key: 'sub7',
            icon: <ControlOutlined />,
            label: 'Acces Control',
            children: [
              {
                key: '/access-control/employer',
                icon: <UserOutlined />,
                label: <Link to='/access-control/employer'>Personal</Link>,
              },
              {
                key: '/access-control/device',
                icon: <ControlOutlined />,
                label: <Link to='/access-control/device'>Tip Device</Link>,
              },
              {
                key: '/access-control/entry',
                icon: <UserOutlined />,
                label: <Link to='/access-control/entry'>Raport Personal</Link>,
              },
            ],
          },
        ]
      : []),
    ...(packages?.timeKeeper && roles?.includes('admin')
      ? [
          {
            key: 'sub5',
            icon: <FundViewOutlined />,
            label: 'Sistem Pontare',
            children: [
              {
                key: '/time-keeper/employer',
                icon: <UserOutlined />,
                label: <Link to='/time-keeper/employer'>Personal</Link>,
              },
              {
                key: '/time-keeper/systemType',
                icon: <ControlOutlined />,
                label: <Link to='/time-keeper/systemType'>Tip Sistem</Link>,
              },
              {
                key: '/time-keeper/device',
                icon: <ControlOutlined />,
                label: <Link to='/time-keeper/device'>Tip Device</Link>,
              },
              {
                key: '/time-keeper/entry',
                icon: <UserOutlined />,
                label: <Link to='/time-keeper/entry'>Raport Personal</Link>,
              },
            ],
          },
        ]
      : []),
    ...((packages?.finance && roles?.includes('admin')) || roles?.includes('accountant')
      ? [
          {
            key: 'sub6',
            icon: <WalletOutlined />,
            label: 'Financiar',
            children: [
              {
                key: '/cashflow/contract',
                icon: <UserOutlined />,
                label: <Link to='/cashflow/contract'>Contracte</Link>,
              },
              {
                key: '/cashflow/offer',
                icon: <ControlOutlined />,
                label: <Link to='/cashflow/offer'>Oferte</Link>,
              },
            ],
          },
        ]
      : []),
    ...(packages?.kpi &&
    (roles?.includes('admin') ||
      roles?.includes('kpi_admin') ||
      roles?.includes('kpi_financial') ||
      roles?.includes('kpi_sales') ||
      roles?.includes('kpi_operations') ||
      roles?.includes('kpi_it') ||
      roles?.includes('kpi_hr') ||
      roles?.includes('kpi_alert') ||
      roles?.includes('kpi_summary') ||
      roles?.includes('kpi_marketing'))
      ? [
          {
            key: 'sub8',
            icon: <InsertRowAboveOutlined />,
            label: 'KPI',
            children: [
              {
                key: 'kpi-administration',
                label: 'KPI Administrare',
                icon: <ContactsOutlined />,
                children: [
                  ...(roles?.includes('kpi_admin') || roles?.includes('kpi_financial')
                    ? [
                        {
                          key: '/kpi/dynamic?section?=Financial',
                          label: <Link to='/kpi/dynamic?section?=Financial'>KPI Financial</Link>,
                        },
                      ]
                    : []),
                  ...(roles?.includes('kpi_admin') || roles?.includes('kpi_sales')
                    ? [
                        {
                          key: '/kpi/dynamic?section?=Sales',
                          label: <Link to='/kpi/dynamic?section?=Sales'>KPI Sales</Link>,
                        },
                      ]
                    : []),
                  ...(roles?.includes('kpi_admin') || roles?.includes('kpi_operations')
                    ? [
                        {
                          key: '/kpi/dynamic?section?=Operations',
                          label: <Link to='/kpi/dynamic?section?=Operations'>KPI Operations</Link>,
                        },
                      ]
                    : []),
                  ...(roles?.includes('kpi_admin') || roles?.includes('kpi_it')
                    ? [
                        {
                          key: '/kpi/dynamic?section=IT%20%26%20Support',
                          label: <Link to='/kpi/dynamic?section=IT%20%26%20Support'>KPI IT & Support</Link>,
                        },
                      ]
                    : []),
                  ...(roles?.includes('kpi_admin') || roles?.includes('kpi_hr')
                    ? [
                        {
                          key: '/kpi/dynamic?section?=HR (summary)',
                          label: <Link to='/kpi/dynamic?section?=HR (summary)'>KPI HR</Link>,
                        },
                      ]
                    : []),
                  ...(roles?.includes('kpi_admin') || roles?.includes('kpi_alert')
                    ? [
                        {
                          key: '/kpi/dynamic?section?=Alerts / Risks',
                          label: <Link to='/kpi/dynamic?section?=Alerts / Risks'>KPI Alerts / Risks</Link>,
                        },
                      ]
                    : []),
                  ...(roles?.includes('kpi_admin') || roles?.includes('kpi_marketing')
                    ? [
                        {
                          key: '/kpi/dynamic?section?=Marketing',
                          label: <Link to='/kpi/dynamic?section?=Marketing'>KPI Marketing</Link>,
                        },
                      ]
                    : []),
                  ...(roles?.includes('kpi_admin') || roles?.includes('kpi_summary')
                    ? [
                        {
                          key: '/kpi/dynamic?section?=Summary',
                          label: <Link to='/kpi/dynamic?section?=Summary'>KPI Weekly Meeting</Link>,
                        },
                      ]
                    : []),
                ],
              },
              {
                key: 'kpi-admin',
                label: 'KPI Rapoarte',
                icon: <FundProjectionScreenOutlined />,
                children: [
                  ...(roles?.includes('kpi_admin')
                    ? [
                        {
                          key: '/kpi/week',
                          label: <Link to='/kpi/week'>KPI Saptamanal</Link>,
                        },
                        {
                          key: '/kpi/arhive',
                          label: <Link to='/kpi/arhive'>KPI Arhiva</Link>,
                        },
                      ]
                    : []),
                ],
              },
            ],
          },
        ]
      : []),
  ];

  const crmMenuItems = [
    {
      key: 'crm-root',
      icon: <img src={administrare} style={{ height: 15 }} alt='logo' className='logo-image' />,
      label: <div>CRM</div>,
      children: [
        ...(canAccessServiceMaintenanceMenu
          ? [
              {
                key: 'crm-sm',
                icon: <FileOutlined />,
                label: 'Service & Mentenanta',
                children: [
                  ...(crmHasFullAccess
                    ? [
                        {
                          key: '/service-maintenance/offer/active-offers',
                          label: <Link to='/service-maintenance/offer/active-offers'>Oferte</Link>,
                        },
                        {
                          key: '/service-maintenance/contract/active-contracts',
                          label: <Link to='/service-maintenance/contract/active-contracts'>Contracte</Link>,
                        },
                        {
                          key: 'crm-dispatch',
                          icon: <ContainerOutlined />,
                          label: 'Dispecerat',
                          children: [
                            {
                              key: '/service-maintenance/dispatch/dispatch',
                              label: <Link to='/service-maintenance/dispatch/dispatch'>Solicitari</Link>,
                            },
                            {
                              key: '/service-maintenance/dispatch/maintenancePlanning',
                              label: (
                                <Link to='/service-maintenance/dispatch/maintenancePlanning'>
                                  Planificare Mentenante
                                </Link>
                              ),
                            },
                            {
                              key: '/service-maintenance/dispatch/myMaintenancePlannings',
                              label: (
                                <Link to='/service-maintenance/dispatch/myMaintenancePlannings'>Mentenantele Mele</Link>
                              ),
                            },
                            {
                              key: '/service-maintenance/interventionFiles',
                              label: <Link to='/service-maintenance/interventionFiles'>Fise de Interventie</Link>,
                            },
                            {
                              key: '/service-maintenance/interventionFile/supplies',
                              label: <Link to='/service-maintenance/interventionFile/supplies'>Consumabile</Link>,
                            },
                            {
                              key: '/service-maintenance/interventionFile/recommendations',
                              label: (
                                <Link to='/service-maintenance/interventionFile/recommendations'>Recomandari</Link>
                              ),
                            },
                          ],
                        },
                      ]
                    : []),
                  ...(crmHasLimitedAccess && (isTechnician || isSeniorTechnician)
                    ? [
                        {
                          key: 'crm-dispatch',
                          icon: <ContainerOutlined />,
                          label: 'Dispecerat',
                          children: [
                            {
                              key: '/service-maintenance/dispatch/dispatch',
                              label: <Link to='/service-maintenance/dispatch/dispatch'>Solicitari</Link>,
                            },
                            {
                              key: '/service-maintenance/dispatch/myMaintenancePlannings',
                              label: (
                                <Link to='/service-maintenance/dispatch/myMaintenancePlannings'>Mentenantele Mele</Link>
                              ),
                            },
                            {
                              key: '/service-maintenance/interventionFiles',
                              label: <Link to='/service-maintenance/interventionFiles'>Fise de Interventie</Link>,
                            },
                          ],
                        },
                      ]
                    : []),
                ],
              },
            ]
          : []),

        ...(crmHasFullAccess
          ? [
              {
                key: 'crm-partnership',
                icon: <ContactsOutlined />,
                label: 'Cadru parteneriat',
                children: [
                  {
                    key: '/panel/company',
                    label: <Link to='/panel/company'>Clienti</Link>,
                  },
                  {
                    key: '/panel/workPlace',
                    label: <Link to='/panel/workPlace'>Puncte de lucru</Link>,
                  },

                  {
                    key: '/panel/agent',
                    label: <Link to='/panel/agent'>Persoane de contact</Link>,
                  },
                ],
              },
              {
                key: 'crm-services',
                icon: <ControlOutlined />,
                label: 'Servicii',
                children: [
                  {
                    key: '/panel/systemType',
                    label: <Link to='/panel/systemType'>Tipuri de sistem</Link>,
                  },
                  {
                    key: '/panel/deviceType',
                    label: <Link to='/panel/deviceType'>Tipuri de device</Link>,
                  },

                  {
                    key: '/panel/labor',
                    label: <Link to='/panel/labor'>Manopera</Link>,
                  },
                  {
                    key: '/panel/laborExtra',
                    label: <Link to='/panel/laborExtra'>Manopera Extra</Link>,
                  },
                ],
              },
              ...(crmCanAccessUsers
                ? [
                    {
                      key: 'crm-profile',
                      icon: <UserOutlined />,
                      label: 'Profil',
                      children: [
                        {
                          key: '/panel/user',
                          label: <Link to='/panel/user'>Utilizatori</Link>,
                        },
                      ],
                    },
                  ]
                : []),
            ]
          : []),
      ],
    },
    ...(canAccessErpMenu
      ? [
          {
            key: 'erp-root',
            icon: <img src={administrare} style={{ height: 15 }} alt='logo' className='logo-image' />,
            label: <div>ERP</div>,
            children: [
              {
                key: '/panel/erp',
                label: <Link to='/panel/erp'>Administrare</Link>,
              },
            ],
          },
        ]
      : []),
  ];

  const menuItems = CRM_ONLY_MODE ? crmMenuItems : fallbackMenuItems;

  if (CRM_ONLY_MODE && profile && !isCrmAllowedRoute(location.pathname, roles)) {
    return <Navigate to={CRM_DEFAULT_ROUTE} replace />;
  }

  if (loading || isUpdatingSignature || isUpdatingPassword) {
    return <Spin size='large' className='spinner' />;
  }

  return (
    <Layout className={`layout ${loading ? 'no-animation' : ''}`}>
      <Header className='header'>
        <div className='home-menu-link '>
          {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
            className: 'trigger',
            onClick: toggle,
          })}
          <div className='logo'>
            <Link to='/home' className='home-menu-link'>
              <img src={logo} alt='logo' className='logo-image' />
            </Link>
          </div>
        </div>
        <div>
          <div className='header-actions'>
            <Badge count={notificationCount} size='small' offset={[-5, 5]}>
              <div className='notification-icon' onClick={() => navigate('/notifications')}>
                <BellOutlined style={{ fontSize: '16px', color: '#ffffff' }} />
              </div>
            </Badge>
            <Dropdown trigger={['hover']} placement='bottom' menu={{ items: userProfileMenu }}>
              <div className='profile-container'>
                <Avatar icon={<UserOutlined />} className='avatar' />
              </div>
            </Dropdown>
          </div>
          <Modal title='Change Password' open={isPasswordModalVisible} onOk={handlePasswordOk} onCancel={handleCancel}>
            <Input.Password
              placeholder='Enter new password'
              value={password}
              onChange={(e) => dispatch(updateField({ field: 'password', value: e.target.value ?? null }))}
            />
          </Modal>

          {/* Signature Modal */}
          <Modal
            title='Change Signature'
            open={isSignatureModalVisible}
            onOk={handleSignatureOk}
            onCancel={handleCancel}
          >
            <SignaturePad
              canUploadSignature={true}
              valueSignature={signature!}
              onSignatureDrawn={(e) => dispatch(updateField({ field: 'signature', value: e ?? null }))}
            />{' '}
          </Modal>
        </div>
      </Header>
      <Layout className='inner-layout'>
        <Sider trigger={null} width={220} collapsible collapsed={collapsed} collapsedWidth={50} className='sider'>
          <Menu
            theme='dark'
            mode='inline'
            selectedKeys={[selectedKey]}
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            onClick={handleMenuClick}
            style={{ background: '#174279' }}
            items={menuItems}
          />
        </Sider>
        <Layout>
          <Content className='content'>{children}</Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default Dashboard;
